import classNames from 'classnames';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { DEPTH, FavoriteCoinType } from 'src/common/constants';
import {
    changeScrollBehavior,
    currencyFormatter,
    currencyValue,
    digits,
    isEmptyObj,
    msToTime,
} from 'src/common/helpers';
import { Tooltip, SimplifiedOrProTradeSwitcher } from 'src/components/core';
import {
    ArrowDown,
    Favorites,
    Day,
    Operations,
    More,
    Night,
    MoreDots,
    Favorite,
} from 'src/components/core/icons';
import { FlexContainer } from 'src/components/core/layout/FlexContainer';
import { useAuth } from 'src/context/AuthContext';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import { APP_URLS } from 'src/setup/client/app-urls';
import { updateThemeData } from 'src/setup/redux/slices/globalSlice';
import { createFavorite, cancelFavorite } from 'src/setup/redux/slices/userSlice';
import { CurrencyValue, DepthType, TradeSocketResponseData } from 'src/types';

import {
    PairInfoName,
    PairInfoOperationIcon,
    PairListIndex,
    PairListIInfo,
} from './mobilePairInfo';
import {
    PairInfoContainer,
    PairName,
    Bar,
    PairInfoModuleWrapper,
    PairInfoModule,
    ThemeSwitchWrapper,
} from './pairInfo';
import { CoinDrawer } from '../coin-drawer';
import { ContractDetail } from '../contract-detail';
import { PreferencesDrawer } from '../preferences-drawer';

const PairInfo: FC = () => {
    const { t } = useTranslation();
    const { isMobile, colors, isDark } = useTheme();
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [isDrawerPreferencesOpen, setIsPreferencesDrawerOpen] = useState(false);
    const [isContractDetailOpen, setIsContractDetailOpen] = useState(false);
    const [tokenInfo, setTokenInfo] = useState({} as TradeSocketResponseData);
    const tokenInfoData = useAppSelector((state) => state.socket.slowBroker);
    const { futuresUnderlying, supportLanguages } = useAppSelector(
        (state) => state.global.configV2,
    );
    const { tokenRates } = useAppSelector((state) => state.global);
    const { currentSymbol, fundingRate } = useAppSelector((state) => state.derivatives);
    const { favorites } = useAppSelector((state) => state.user);

    const { isAuthenticated } = useAuth();

    const time = msToTime(fundingRate ? (fundingRate.fixTime as number) : 0);

    const minPricePrecision: keyof DepthType = currentSymbol?.minPricePrecision || '0';

    const dispatch = useAppDispatch();

    useEffect(() => {
        let infoObj = tokenInfoData[currentSymbol.symbolId];
        if (infoObj) {
            setTokenInfo(infoObj);
        } else {
            setTokenInfo({} as TradeSocketResponseData);
        }
    }, [tokenInfoData, currentSymbol]);

    const changeThemeMode = () => {
        dispatch(updateThemeData({ isDark: !isDark }));
    };

    const drawerToggler = () => {
        changeScrollBehavior(!isDrawerOpen);
        setIsDrawerOpen(!isDrawerOpen);
    };

    const currencyValueCal = () => {
        if (!isEmptyObj(tokenRates) && supportLanguages && !isEmptyObj(tokenInfo)) {
            let valueInCurrency: CurrencyValue = currencyValue(
                tokenRates[currentSymbol.quoteTokenId],
                Number(tokenInfo.c),
                supportLanguages,
                true,
            );
            return `≈${valueInCurrency.value} ${valueInCurrency.endName}`;
        }
        return '--';
    };

    const favoriteClicked = (symbolId: string) => {
        const body = {
            symbol_ids: symbolId,
            coin_type: FavoriteCoinType.FUTURE,
            exchange_id: currentSymbol.exchangeId,
        };
        if (!favorites.includes(symbolId)) {
            dispatch(createFavorite({ body, isLogin: isAuthenticated }));
        } else {
            dispatch(cancelFavorite({ body, isLogin: isAuthenticated }));
        }
    };

    const togglePreferencesDrawer = () => {
        setIsPreferencesDrawerOpen(!isDrawerPreferencesOpen);
        changeScrollBehavior(!isDrawerPreferencesOpen);
    };

    const handleContractDetail = () => {
        setIsContractDetailOpen(!isContractDetailOpen);
    };

    return (
        <>
            {isDrawerOpen && (
                <CoinDrawer drawerToggler={drawerToggler} isDrawerOpen={isDrawerOpen} />
            )}
            {isMobile ? (
                <FlexContainer
                    styles={{
                        flexDirection: 'column',
                        padding: '24.5px 16px 16px',
                    }}
                >
                    <FlexContainer styles={{ justifyContent: 'flex-end', width: '100%' }}>
                        <SimplifiedOrProTradeSwitcher isDerivative={true} />
                    </FlexContainer>
                    <FlexContainer
                        styles={{
                            width: '100%',
                            justifyContent: 'space-between',
                            marginBottom: '10px',
                            padding: '8.5px',
                        }}
                    >
                        <PairInfoName>
                            <More
                                fill={colors.text.primary}
                                stroke={colors.text.primary}
                                className={'clickable'}
                                onClick={drawerToggler}
                            />
                            <span>
                                <span>{currentSymbol.secondLevelUnderlyingName}</span>
                                {currentSymbol.quoteTokenName}-P
                            </span>
                        </PairInfoName>
                        <PairInfoOperationIcon>
                            {isDark ? (
                                <Day
                                    stroke={colors.text.primary}
                                    onClick={changeThemeMode}
                                    className={'clickable'}
                                />
                            ) : (
                                <Night
                                    stroke={colors.text.primary}
                                    onClick={changeThemeMode}
                                    className={'clickable'}
                                />
                            )}
                            <Operations
                                stroke={colors.text.primary}
                                className={'clickable'}
                                onClick={handleContractDetail}
                            />
                            {isAuthenticated && (
                                <MoreDots
                                    stroke={colors.text.primary}
                                    className={'clickable'}
                                    onClick={togglePreferencesDrawer}
                                />
                            )}
                            <PreferencesDrawer
                                isOpen={isDrawerPreferencesOpen}
                                toggleDrawer={togglePreferencesDrawer}
                            />
                            <ContractDetail
                                isContractDetailOpen={isContractDetailOpen}
                                handleContractDetail={handleContractDetail}
                            />
                        </PairInfoOperationIcon>
                    </FlexContainer>
                    <FlexContainer
                        styles={{
                            width: '100%',
                            justifyContent: 'space-between',
                            marginBottom: '10px',
                            padding: '8.5px',
                        }}
                    >
                        <PairListIndex>
                            <li
                                className={
                                    Number(tokenInfo?.m) >= 0
                                        ? 'up'
                                        : Number(tokenInfo?.m) < 0
                                        ? 'down'
                                        : ''
                                }
                            >
                                <div>
                                    {tokenInfo.c
                                        ? currencyFormatter(
                                              digits(Number(tokenInfo.c), DEPTH[minPricePrecision]),
                                              DEPTH[minPricePrecision],
                                          )
                                        : '0.00'}
                                </div>
                            </li>
                            <li>
                                <>
                                    {currencyValueCal()}
                                    <span className={Number(tokenInfo.m) >= 0 ? 'up' : 'down'}>
                                        {Number(tokenInfo.m) > 0 ? '+' : ''}
                                        {!isNaN(Number(tokenInfo?.m))
                                            ? `${currencyFormatter(
                                                  Math.floor(Number(tokenInfo.m) * 10000) / 100,
                                                  2,
                                              )}%`
                                            : '--'}
                                    </span>
                                </>
                            </li>
                        </PairListIndex>
                        <FlexContainer>
                            <PairListIInfo>
                                <li>{t('24_high')}</li>
                                <li>{t('24_low')}</li>
                                <li>{t('24_turn_over')}</li>
                            </PairListIInfo>
                            <PairListIInfo>
                                <li>
                                    {Number(tokenInfo?.h) >= 0
                                        ? currencyFormatter(
                                              digits(Number(tokenInfo.h), DEPTH[minPricePrecision]),
                                              DEPTH[minPricePrecision],
                                          )
                                        : '--'}
                                </li>
                                <li>
                                    {Number(tokenInfo?.l) >= 0
                                        ? currencyFormatter(
                                              digits(Number(tokenInfo.l), DEPTH[minPricePrecision]),
                                              DEPTH[minPricePrecision],
                                          )
                                        : '--'}
                                </li>
                                <li>
                                    {Number(tokenInfo?.qv) >= 0
                                        ? `$${currencyFormatter(
                                              Number(tokenInfo.qv),
                                              DEPTH[minPricePrecision],
                                          )}`
                                        : '--'}
                                </li>
                            </PairListIInfo>
                        </FlexContainer>
                    </FlexContainer>
                </FlexContainer>
            ) : (
                <FlexContainer
                    styles={{
                        justifyContent: 'space-between',
                        padding: '8px 32px 8px 24px',
                        height: '51px',
                        borderBottom: `1px solid ${colors.border.light}`,
                        borderTop: `1px solid ${colors.border.light}`,
                    }}
                >
                    <PairInfoContainer>
                        <FlexContainer
                            className="PairInfo"
                            styles={{ cursor: 'pointer' }}
                            onClick={drawerToggler}
                        >
                            <PairName>
                                <div>
                                    <span>
                                        <span>{currentSymbol.secondLevelUnderlyingName}</span>
                                        {currentSymbol.quoteTokenName}-P
                                    </span>
                                    <span>{futuresUnderlying && futuresUnderlying[0]?.name}</span>
                                </div>
                                {currentSymbol.label && currentSymbol.label.labelValue ? (
                                    <div
                                        style={
                                            currentSymbol.label.colorCode
                                                ? {
                                                      color: currentSymbol.label.colorCode,
                                                      borderColor: currentSymbol.label.colorCode,
                                                  }
                                                : {}
                                        }
                                    >
                                        {currentSymbol.label.labelValue}
                                    </div>
                                ) : (
                                    ''
                                )}
                                <ArrowDown stroke={colors.text.primary} className={'clickable'} />
                            </PairName>
                        </FlexContainer>
                        <Bar height={'36px'} margin={'0 16px'} />
                        <PairInfoModule
                            className={classNames(
                                Number(tokenInfo?.m) >= 0
                                    ? 'up'
                                    : Number(tokenInfo?.m) < 0
                                    ? 'down'
                                    : '',
                                'heading',
                            )}
                        >
                            {tokenInfo.c
                                ? currencyFormatter(
                                      digits(Number(tokenInfo.c), DEPTH[minPricePrecision]),
                                      DEPTH[minPricePrecision],
                                  )
                                : '0.00'}
                        </PairInfoModule>
                        <PairInfoModuleWrapper>
                            <PairInfoModule>
                                <span>{`${t('24h_change')} %`}</span>
                                <span
                                    className={
                                        Number(tokenInfo.m) >= 0
                                            ? 'up'
                                            : Number(tokenInfo.m) < 0
                                            ? 'down'
                                            : ''
                                    }
                                >
                                    {Number(tokenInfo.m) > 0 ? '+' : ''}
                                    {!isNaN(Number(tokenInfo.m))
                                        ? `${currencyFormatter(
                                              Math.floor(Number(tokenInfo.m) * 10000) / 100,
                                              2,
                                          )}%`
                                        : '--'}
                                </span>
                            </PairInfoModule>
                            <PairInfoModule>
                                <span>{t('24_high')}</span>
                                <span>
                                    {Number(tokenInfo?.h)
                                        ? currencyFormatter(
                                              digits(Number(tokenInfo.h), DEPTH[minPricePrecision]),
                                              DEPTH[minPricePrecision],
                                          )
                                        : '--'}
                                </span>
                            </PairInfoModule>
                            <PairInfoModule>
                                <span>{t('24_low')}</span>
                                <span>
                                    {Number(tokenInfo?.l)
                                        ? currencyFormatter(
                                              digits(Number(tokenInfo.l), DEPTH[minPricePrecision]),
                                              DEPTH[minPricePrecision],
                                          )
                                        : '--'}
                                </span>
                            </PairInfoModule>
                            <PairInfoModule>
                                <span>{t('24_volume')}</span>
                                <span>
                                    {Number(tokenInfo?.v) >= 0
                                        ? currencyFormatter(
                                              digits(
                                                  Number(tokenInfo.v),
                                                  DEPTH[currentSymbol?.basePrecision],
                                              ),
                                              DEPTH[currentSymbol?.basePrecision],
                                          )
                                        : '--'}
                                </span>
                            </PairInfoModule>
                            <PairInfoModule>
                                <span>
                                    {t('24_turnover', { token: currentSymbol.quoteTokenName })}
                                </span>
                                <span>
                                    {Number(tokenInfo?.qv) >= 0
                                        ? currencyFormatter(
                                              Number(tokenInfo.qv),
                                              DEPTH[minPricePrecision],
                                          )
                                        : '--'}
                                </span>
                            </PairInfoModule>
                            <PairInfoModule>
                                <span className={'funding-rate'}>
                                    <Tooltip
                                        overlay={
                                            <span>
                                                {t('funding_rate_definition')}&nbsp;
                                                <a
                                                    href={
                                                        APP_URLS.FUNDING_RATE_DETAIL_URL +
                                                        currentSymbol.symbolId
                                                    }
                                                    target={'blank'}
                                                >
                                                    {t('more_details')}
                                                </a>
                                            </span>
                                        }
                                        interactive
                                    >
                                        <>{t('funding_rate')}</>
                                    </Tooltip>
                                    /{' '}
                                    <Tooltip overlay={t('next_settlement')} placement="bottom">
                                        <>{t('countdown')}</>
                                    </Tooltip>
                                </span>
                                <span>
                                    <span className={'index'}>
                                        {fundingRate
                                            ? currencyFormatter(
                                                  digits(Number(fundingRate.fundingRate) * 100, 4),
                                                  4,
                                              )
                                            : '0.0000'}
                                        %
                                    </span>{' '}
                                    / {time[1]}:{time[2]}:{time[3]}
                                </span>
                            </PairInfoModule>
                        </PairInfoModuleWrapper>
                    </PairInfoContainer>
                    <PairInfoContainer>
                        <ThemeSwitchWrapper>
                            <SimplifiedOrProTradeSwitcher isDerivative={true} />

                            <Bar height={'20px'} margin={'0 12px'} />
                            {favorites.includes(currentSymbol.symbolId) ? (
                                <Favorites
                                    stroke={colors.primary}
                                    fill={colors.primary}
                                    onClick={() => favoriteClicked(currentSymbol.symbolId)}
                                    className={'clickable'}
                                />
                            ) : (
                                <Favorite
                                    stroke={colors.text.primary}
                                    onClick={() => favoriteClicked(currentSymbol.symbolId)}
                                    className={'clickable'}
                                />
                            )}

                            <Bar height={'20px'} margin={'0 12px'} />
                            {isDark ? (
                                <Day
                                    stroke={colors.text.primary}
                                    onClick={changeThemeMode}
                                    className={'clickable'}
                                />
                            ) : (
                                <Night
                                    stroke={colors.text.primary}
                                    onClick={changeThemeMode}
                                    className={'clickable'}
                                />
                            )}
                        </ThemeSwitchWrapper>
                    </PairInfoContainer>
                </FlexContainer>
            )}
        </>
    );
};

export { PairInfo };
